import type { CSSProperties, FC, ReactNode } from 'react'
import type { ChartAnimation } from '@/components/charts/chart-utils'

import { cn } from '@/lib/utils'

interface ProgressBarProps {
    value: number
    color?: string
    marker?: number
    size?: 'sm' | 'md'
    animation?: ChartAnimation
    delay?: number
    surface?: 'light' | 'dark'
    label?: string
    className?: string
}

/** Horizontal meter. value and marker are fractions between 0 and 1 (values above 1 are capped). */
export const ProgressBar: FC<ProgressBarProps> = ({
    value,
    color = '#391A18',
    marker,
    size = 'sm',
    animation = 'inview',
    delay = 0,
    surface = 'light',
    label,
    className
}): ReactNode => {
    const clamped = Math.min(1, Math.max(0, value))

    return (
        <div
            role={label ? 'meter' : undefined}
            aria-label={label}
            aria-valuemin={label ? 0 : undefined}
            aria-valuemax={label ? 100 : undefined}
            aria-valuenow={label ? Math.round(value * 100) : undefined}
            className={cn(
                'relative w-full rounded-full',
                size === 'sm' ? 'h-1.5' : 'h-2.5',
                surface === 'dark' ? 'bg-cream/10' : 'bg-burgundy/[0.07]',
                animation === 'autoplay' && 'chart-autoplay',
                className
            )}
            data-chart={animation === 'manual' ? undefined : ''}
            data-inview={animation === 'autoplay' ? 'true' : undefined}
        >
            <span
                data-progress=''
                className='chart-grow-x absolute inset-y-0 left-0 rounded-full'
                style={{
                    width: `${clamped * 100}%`,
                    backgroundColor: color,
                    transition: 'transform 1.3s var(--ease-out-expo) var(--chart-delay, 0ms), width 0.8s var(--ease-out-expo), background-color 0.4s ease',
                    '--chart-delay': `${delay}ms`
                } as CSSProperties}
            />

            {marker !== undefined && (
                <span
                    aria-hidden='true'
                    className={cn('absolute -top-1 -bottom-1 w-px', surface === 'dark' ? 'bg-cream/50' : 'bg-burgundy/40')}
                    style={{ left: `${Math.min(1, Math.max(0, marker)) * 100}%` }}
                />
            )}
        </div>
    )
}
